// Shared editor for Shopify products and services.
// Used by AdminProducts and AdminShopServices.

import { useEffect, useState } from 'react';
import { type ShopifyProductNode, metafieldMap, resolveFaqsFromMetafield, RIHAM_METAFIELD_KEYS } from '@/lib/shopify';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Pencil, RefreshCw, Plus, Trash2, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { servicesData, productsData, shopCategories, serviceCategories } from '@/data/content';


async function shopifyAdmin<T = any>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session?.access_token) {
    throw new Error('נדרש להתחבר מחדש לניהול');
  }
  const { data, error } = await supabase.functions.invoke('shopify-admin', {
    body: { query, variables },
  });
  if (error) {
    const ctx: any = (error as any).context;
    const status = ctx?.status;
    if (status === 401 || status === 403) {
      throw new Error('אין הרשאה לניהול החנות');
    }
    let text = '';
    try {
      text = ctx ? await ctx.text() : '';
    } catch {
      // ignore
    }
    throw new Error(text || error.message || 'Shopify Admin request failed');
  }
  const json = data as any;
  if (json?.errors?.length)
    throw new Error(json.errors.map((e: any) => e.message).join(', '));
  const ue =
    json?.data?.productUpdate?.userErrors ??
    json?.data?.productVariantUpdate?.userErrors ??
    json?.data?.metafieldsSet?.userErrors ??
    json?.data?.productCreateMedia?.mediaUserErrors ??
    json?.data?.productDeleteMedia?.mediaUserErrors ??
    json?.data?.metaobjectUpdate?.userErrors ??
    json?.data?.metaobjectCreate?.userErrors;
  if (ue?.length) throw new Error(ue.map((e: any) => e.message).join(', '));
  return json?.data as T;
}

const UPDATE_PRODUCT = `
  mutation ProductUpdate($input: ProductInput!) {
    productUpdate(input: $input) {
      product { id title descriptionHtml }
      userErrors { field message }
    }
  }
`;

const UPDATE_VARIANT = `
  mutation VariantUpdate($input: ProductVariantInput!) {
    productVariantUpdate(input: $input) {
      productVariant { id price compareAtPrice }
      userErrors { field message }
    }
  }
`;

const METAFIELDS_SET = `
  mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
    metafieldsSet(metafields: $metafields) {
      metafields { id key namespace value }
      userErrors { field message }
    }
  }
`;

const PRODUCT_CREATE_MEDIA = `
  mutation ProductCreateMedia($productId: ID!, $media: [CreateMediaInput!]!) {
    productCreateMedia(productId: $productId, media: $media) {
      media { ... on MediaImage { id } }
      mediaUserErrors { field message }
    }
  }
`;

const PRODUCT_DELETE_MEDIA = `
  mutation ProductDeleteMedia($productId: ID!, $mediaIds: [ID!]!) {
    productDeleteMedia(productId: $productId, mediaIds: $mediaIds) {
      deletedMediaIds
      mediaUserErrors { field message }
    }
  }
`;

const METAOBJECT_GET = `
  query MO($id: ID!) {
    metaobject(id: $id) { id type fields { key value } }
  }
`;

const METAOBJECT_UPDATE = `
  mutation MetaobjectUpdate($id: ID!, $metaobject: MetaobjectUpdateInput!) {
    metaobjectUpdate(id: $id, metaobject: $metaobject) {
      metaobject { id }
      userErrors { field message code }
    }
  }
`;

const METAOBJECT_CREATE = `
  mutation MetaobjectCreate($metaobject: MetaobjectCreateInput!) {
    metaobjectCreate(metaobject: $metaobject) {
      metaobject { id }
      userErrors { field message code }
    }
  }
`;


// We need media IDs (admin API) for deletion. Storefront only returns URLs.
const PRODUCT_MEDIA_QUERY = `
  query ProductMedia($id: ID!) {
    product(id: $id) {
      media(first: 50) {
        edges { node {
          ... on MediaImage { id image { url } }
        } }
      }
    }
  }
`;

// Fetch ALL products (incl. unpublished services) via admin API.
// NOTE: Admin API `metafields` does NOT accept `identifiers` (that's Storefront-only).
// We use aliased `metafield(namespace, key)` lookups instead.
const ADMIN_METAFIELD_ALIASES = RIHAM_METAFIELD_KEYS.map(
  (k) => `${k}: metafield(namespace: "riham", key: "${k}") { value type }`,
).join('\n          ');

const ADMIN_PRODUCTS_QUERY = `
  query AdminProducts($first: Int!) {
    products(first: $first, sortKey: TITLE) {
      edges { node {
        id title descriptionHtml handle tags productType
        collections(first: 10) { edges { node { id title handle } } }
        images(first: 10) { edges { node { url altText } } }
        variants(first: 10) { edges { node {
          id title sku availableForSale
          price compareAtPrice
          selectedOptions { name value }
        } } }
        ${ADMIN_METAFIELD_ALIASES}
      } }
    }
  }
`;

// Convert aliased metafield fields back into the array shape used by metafieldMap().
function adminMetafieldsToArray(n: any) {
  return RIHAM_METAFIELD_KEYS.map((k) => {
    const m = n?.[k];
    return m
      ? { key: k, namespace: 'riham', value: m.value, type: m.type ?? '' }
      : null;
  });
}

// Metafield definitions required so the Storefront API returns these values.
// Without a definition that has storefront access = PUBLIC_READ, Shopify
// returns null from `metafields(identifiers: ...)` on the storefront and
// fields like FAQ never reach the frontend.
const METAFIELD_DEF_TYPES: Record<string, string> = {
  subtitle: 'single_line_text_field',
  tagline: 'single_line_text_field',
  size: 'single_line_text_field',
  long_description: 'list.single_line_text_field',
  ingredients: 'multi_line_text_field',
  usage: 'multi_line_text_field',
  how_to_use: 'multi_line_text_field',
  benefits: 'list.single_line_text_field',
  faqs: 'list.metaobject_reference', // Shopify field type is Metaobject (List)
  video_url: 'url',
  duration: 'single_line_text_field',
  suitable_for: 'multi_line_text_field',
  before_treatment: 'multi_line_text_field',
  after_treatment: 'multi_line_text_field',
};

const METAFIELD_DEF_CREATE = `
  mutation Create($definition: MetafieldDefinitionInput!) {
    metafieldDefinitionCreate(definition: $definition) {
      createdDefinition { id key }
      userErrors { field message code }
    }
  }
`;

let metafieldDefsEnsured = false;
async function ensureMetafieldDefs() {
  if (metafieldDefsEnsured) return;
  for (const key of RIHAM_METAFIELD_KEYS) {
    const type = METAFIELD_DEF_TYPES[key];
    if (!type) continue;
    try {
      await shopifyAdmin(METAFIELD_DEF_CREATE, {
        definition: {
          namespace: 'riham',
          key,
          name: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          type,
          ownerType: 'PRODUCT',
          access: { storefront: 'PUBLIC_READ' },
        },
      });
    } catch (e) {
      // Idempotent: ignore "already taken" / duplicate-key errors.
      const msg = (e as Error).message ?? '';
      if (!/TAKEN|already exists|already taken/i.test(msg)) {
        console.warn(`ensureMetafieldDefs ${key}:`, msg);
      }
    }
  }
  metafieldDefsEnsured = true;
}


export type ProductCollection = { id: string; title: string; handle: string };

export function productCollections(p: ShopifyProductNode): ProductCollection[] {
  const edges = (p as any)._collections as Array<{ node: ProductCollection }> | undefined;
  return edges?.map((e) => e.node) ?? [];
}

// Normalize admin API product shape to match Storefront ShopifyProductNode.
function normalizeAdminProduct(n: any): ShopifyProductNode {
  return {
    id: n.id,
    title: n.title,
    description: n.descriptionHtml ?? '',
    handle: n.handle,
    tags: n.tags ?? [],
    productType: n.productType ?? '',
    priceRange: {
      minVariantPrice: {
        amount: n.variants?.edges?.[0]?.node?.price ?? '0',
        currencyCode: 'ILS',
      },
    },
    images: n.images,
    variants: {
      edges: (n.variants?.edges ?? []).map((e: any) => ({
        node: {
          id: e.node.id,
          title: e.node.title,
          sku: e.node.sku ?? '',
          availableForSale: e.node.availableForSale,
          price: { amount: e.node.price ?? '0', currencyCode: 'ILS' },
          compareAtPrice: e.node.compareAtPrice
            ? { amount: e.node.compareAtPrice, currencyCode: 'ILS' }
            : null,
          selectedOptions: e.node.selectedOptions ?? [],
        },
      })),
    },
    metafields: adminMetafieldsToArray(n),
    // Carry-through fields for buildDraft and collection-based filtering.
    ...({ descriptionHtml: n.descriptionHtml, _collections: n.collections?.edges } as any),
  };
}



type FaqItem = { q: string; a: string };

type Draft = {
  productId: string;
  variantId: string;
  title: string;
  descriptionHtml: string;
  price: string;
  compareAtPrice: string;
  sku: string;
  imageUrls: string[];
  productType: string;
  tags: string[];
  serviceCategory: string;
  subtitle: string;
  tagline: string;
  size: string;
  longDescription: string;
  ingredients: string;
  howToUse: string;
  benefits: string[];
  faq: FaqItem[];
  faqGids: string[]; // metaobject GIDs for list.metaobject_reference
  videoUrl: string;
  duration: string;
  suitableFor: string;
  beforeTreatment: string;
  afterTreatment: string;
};

// `riham.long_description` is defined in Shopify as list.single_line_text_field
// (a JSON array of paragraphs). The editor exposes it as one textarea, so we
// convert both ways and also tolerate legacy plain-text values.
function paragraphListToText(value: string | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((x): x is string => typeof x === 'string')
          .map((x) => x.trim())
          .filter(Boolean)
          .join('\n\n');
      }
    } catch {
      /* fall through */
    }
  }
  return raw;
}

function textToParagraphList(text: string): string {
  const paragraphs = (text ?? '')
    .split(/\n\s*\n/)
    .map((x) => x.trim())
    .filter(Boolean);
  return JSON.stringify(paragraphs);
}


function buildDraft(sp: ShopifyProductNode): Draft {
  const variant = sp.variants.edges[0]?.node;
  const mf = metafieldMap(sp);
  const parseJson = <T,>(s: string | undefined, fallback: T): T => {
    if (!s) return fallback;
    try {
      return JSON.parse(s) as T;
    } catch {
      return fallback;
    }
  };


  // Extract FAQ GIDs from metaobject list metafield
  const faqsMetafield = sp.metafields?.find((m) => m?.key === 'faqs');
  const faqGids: string[] = (() => {
    if (!faqsMetafield?.value) return [];
    try {
      const parsed = JSON.parse(faqsMetafield.value);
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        typeof parsed[0] === 'string' &&
        parsed[0].startsWith('gid://')
      ) {
        return parsed;
      }
    } catch {}
    return [];
  })();

  const resolvedFaqs = resolveFaqsFromMetafield(faqsMetafield, []);

  return {
    productId: sp.id,
    variantId: variant?.id ?? '',
    title: sp.title,
    descriptionHtml: (sp as any).descriptionHtml ?? sp.description ?? '',
    price: variant?.price.amount ?? '',
    compareAtPrice: variant?.compareAtPrice?.amount ?? '',
    sku: variant?.sku ?? '',
    imageUrls: sp.images.edges.map((e) => e.node.url),
    productType: sp.productType ?? '',
    tags: sp.tags ?? [],
    serviceCategory: (() => {
      const t = (sp.tags ?? []).find((x) =>
        x.toLowerCase().startsWith('service-category-'),
      );
      return t ? t.slice('service-category-'.length).toLowerCase() : '';
    })(),
    subtitle: mf.subtitle ?? '',
    tagline: mf.tagline ?? '',
    size: mf.size ?? '',
    longDescription: paragraphListToText(mf.long_description),
    ingredients: mf.ingredients ?? '',
    howToUse: mf.usage || mf.how_to_use || '',
    benefits: parseJson<string[]>(mf.benefits, []),
    faq: resolvedFaqs,
    faqGids,
    videoUrl: mf.video_url ?? '',
    duration: mf.duration ?? '',
    suitableFor: mf.suitable_for ?? '',
    beforeTreatment: mf.before_treatment ?? '',
    afterTreatment: mf.after_treatment ?? '',
  };
}

// Suggested categories per item type. Pulled from the same source the public
// site uses, so admin labels match what visitors see.
// Products: value = Shopify `productType` (case preserved for Shopify).
// Services: value = slug stored in tag `service-category-<slug>`.
const PRODUCT_CATEGORY_OPTIONS: { value: string; label: string }[] = shopCategories.map(
  (c) => ({ value: c.id.charAt(0).toUpperCase() + c.id.slice(1), label: c.label }),
);
const SERVICE_CATEGORY_OPTIONS: { value: string; label: string }[] = serviceCategories.map(
  (c) => ({ value: c.id, label: c.label }),
);

// Map a raw category key (productType or service-category slug) → site label.
function categoryLabel(itemType: 'product' | 'service', key: string): string {
  if (!key) return '';
  const k = key.toLowerCase();
  const src = itemType === 'service' ? serviceCategories : shopCategories;
  return src.find((c) => c.id.toLowerCase() === k)?.label ?? key;
}

export type ShopifyItemsPageProps = {
  title: string;
  description: string;
  filter?: (p: ShopifyProductNode) => boolean;
  emptyText?: string;
  itemType?: 'product' | 'service';
};

export default function ShopifyItemsPage({
  title,
  description,
  filter,
  emptyText = 'אין פריטים.',
  itemType = 'product',
}: ShopifyItemsPageProps) {
  const [items, setItems] = useState<ShopifyProductNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [original, setOriginal] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryHandle, setCategoryHandle] = useState<string>('all');

  const isService = itemType === 'service';

  const load = async () => {
    setLoading(true);
    try {
      // Ensure storefront-readable metafield definitions exist (idempotent).
      // Without this, FAQ / subtitle / etc. don't reach the frontend.
      ensureMetafieldDefs().catch(() => {});
      const data = await shopifyAdmin<any>(ADMIN_PRODUCTS_QUERY, { first: 100 });
      const edges: any[] = data?.products?.edges ?? [];
      const all = edges.map((e) => normalizeAdminProduct(e.node));
      setItems(filter ? all.filter(filter) : all);
    } catch (e) {
      toast.error('שגיאה בטעינה', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Category source differs by item type:
  // - Products: Shopify `productType` (e.g. "Hair" / "Face" / "Body").
  // - Services: a tag of the form `service-category-<cat>` (productType is
  //   always "Service" for treatments, so we encode the real category in tags).
  const UNCAT = '__uncat__';
  const SERVICE_CAT_PREFIX = 'service-category-';
  const serviceCategoryFromTags = (tags: string[]): string => {
    const t = (tags ?? []).find((x) => x.toLowerCase().startsWith(SERVICE_CAT_PREFIX));
    return t ? t.slice(SERVICE_CAT_PREFIX.length).toLowerCase() : '';
  };
  const categoryKeyOf = (p: ShopifyProductNode) => {
    if (isService) {
      const c = serviceCategoryFromTags(p.tags ?? []);
      return c || UNCAT;
    }
    return p.productType?.trim() ? p.productType.trim() : UNCAT;
  };

  const categories = (() => {
    const map = new Map<string, { key: string; title: string; count: number }>();
    for (const p of items) {
      const k = categoryKeyOf(p);
      const cur = map.get(k);
      map.set(k, {
        key: k,
        title: k === UNCAT ? 'ללא קטגוריה' : categoryLabel(itemType, k),
        count: (cur?.count ?? 0) + 1,
      });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === UNCAT) return 1;
      if (b.key === UNCAT) return -1;
      return a.title.localeCompare(b.title, 'he');
    });
  })();

  const visibleItems = items.filter((p) => {
    if (categoryHandle !== 'all' && categoryKeyOf(p) !== categoryHandle) {
      return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const variant = p.variants.edges[0]?.node;
      const hay = [
        p.title,
        p.handle,
        p.productType,
        variant?.sku ?? '',
        ...(p.tags ?? []),
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Group visible items by category for display
  const groupedItems = (() => {
    const groups = new Map<string, { title: string; items: ShopifyProductNode[] }>();
    for (const p of visibleItems) {
      const k = categoryKeyOf(p);
      const title = k === UNCAT ? 'ללא קטגוריה' : categoryLabel(itemType, k);
      const g = groups.get(k) ?? { title, items: [] };
      g.items.push(p);
      groups.set(k, g);
    }
    return Array.from(groups.entries())
      .map(([key, g]) => ({ key, ...g }))
      .sort((a, b) => {
        if (a.key === UNCAT) return 1;
        if (b.key === UNCAT) return -1;
        return a.title.localeCompare(b.title, 'he');
      });
  })();

  const startEdit = async (sp: ShopifyProductNode) => {
    const d = buildDraft(sp);
    setOriginal(d);
    setEditing({ ...d });

    // FAQ metaobjects can't be read via the Storefront token — resolve them
    // server-side through the shopify-faqs edge function and patch the draft.
    if (d.faqGids.length > 0 && d.faq.length === 0) {
      try {
        const { data, error } = await supabase.functions.invoke('shopify-faqs', {
          body: { ids: d.faqGids },
        });
        if (error) throw error;
        const byId = (data?.byId ?? {}) as Record<
          string,
          { question: string; answer: string }
        >;
        const faq = d.faqGids
          .map((gid) => byId[gid])
          .filter(Boolean)
          .map((x) => ({ q: x.question, a: x.answer }));
        if (faq.length) {
          const patched = { ...d, faq };
          setOriginal(patched);
          setEditing({ ...patched });
        }
      } catch (e) {
        console.error('Failed to resolve FAQs', e);
      }
    }
  };

  const save = async () => {
    if (!editing || !original) return;
    setSaving(true);
    try {
      // 1) Product update (title, description, category, tags)
      const productInput: Record<string, unknown> = {
        id: editing.productId,
        title: editing.title,
        descriptionHtml: editing.descriptionHtml,
      };
      if (editing.productType !== original.productType) {
        productInput.productType = editing.productType;
      }
      // For services, the real category lives in a `service-category-<cat>` tag.
      // If serviceCategory changed, splice it into the tags array before diffing.
      let effectiveTags = editing.tags;
      if (isService && editing.serviceCategory !== original.serviceCategory) {
        const cleaned = effectiveTags.filter(
          (t) => !t.toLowerCase().startsWith('service-category-'),
        );
        effectiveTags = editing.serviceCategory
          ? [...cleaned, `service-category-${editing.serviceCategory.toLowerCase()}`]
          : cleaned;
      }
      const tagsChanged =
        effectiveTags.length !== original.tags.length ||
        effectiveTags.some((t, i) => t !== original.tags[i]);
      if (tagsChanged) productInput.tags = effectiveTags;
      await shopifyAdmin(UPDATE_PRODUCT, { input: productInput });
      // 2) Variant update
      if (editing.variantId) {
        const variantInput: Record<string, unknown> = { id: editing.variantId };
        if (editing.price !== original.price) variantInput.price = editing.price;
        if (editing.compareAtPrice !== original.compareAtPrice)
          variantInput.compareAtPrice = editing.compareAtPrice || null;
        if (editing.sku !== original.sku) variantInput.sku = editing.sku;
        if (Object.keys(variantInput).length > 1) {
          await shopifyAdmin(UPDATE_VARIANT, { input: variantInput });
        }
      }
      // 3) Media diff
      const removed = original.imageUrls.filter((u) => !editing.imageUrls.includes(u));
      const added = editing.imageUrls.filter((u) => !original.imageUrls.includes(u));
      if (added.length) {
        await shopifyAdmin(PRODUCT_CREATE_MEDIA, {
          productId: editing.productId,
          media: added.map((u) => ({
            originalSource: u,
            mediaContentType: 'IMAGE',
          })),
        });
      }
      if (removed.length) {
        // Need media IDs — fetch them
        const media = await shopifyAdmin<any>(PRODUCT_MEDIA_QUERY, {
          id: editing.productId,
        });
        const edges: any[] = media.product?.media?.edges ?? [];
        const idsToDelete = edges
          .filter((e) => removed.includes(e.node?.image?.url))
          .map((e) => e.node.id);
        if (idsToDelete.length) {
          await shopifyAdmin(PRODUCT_DELETE_MEDIA, {
            productId: editing.productId,
            mediaIds: idsToDelete,
          });
        }
      }
      // 4) Metafields
      const mfChanges: Array<{
        ownerId: string;
        namespace: string;
        key: string;
        type: string;
        value: string;
      }> = [];
      const pushIfChanged = (
        key: string,
        type: string,
        next: string,
        prev: string,
      ) => {
        if (next !== prev) {
          mfChanges.push({
            ownerId: editing.productId,
            namespace: 'riham',
            key,
            type,
            value: next,
          });
        }
      };
      pushIfChanged('subtitle', 'single_line_text_field', editing.subtitle, original.subtitle);
      pushIfChanged('tagline', 'single_line_text_field', editing.tagline, original.tagline);
      pushIfChanged('size', 'single_line_text_field', editing.size, original.size);
      pushIfChanged(
        'long_description',
        'list.single_line_text_field',
        textToParagraphList(editing.longDescription),
        textToParagraphList(original.longDescription),
      );
      pushIfChanged('ingredients', 'multi_line_text_field', editing.ingredients, original.ingredients);
      // Canonical key on the storefront is `usage`; `how_to_use` is kept in
      // sync so older data stays consistent.
      pushIfChanged('usage', 'multi_line_text_field', editing.howToUse, original.howToUse);
      pushIfChanged('how_to_use', 'multi_line_text_field', editing.howToUse, original.howToUse);
      pushIfChanged('video_url', 'url', editing.videoUrl, original.videoUrl);
      pushIfChanged('duration', 'single_line_text_field', editing.duration, original.duration);
      pushIfChanged('suitable_for', 'multi_line_text_field', editing.suitableFor, original.suitableFor);
      pushIfChanged('before_treatment', 'multi_line_text_field', editing.beforeTreatment, original.beforeTreatment);
      pushIfChanged('after_treatment', 'multi_line_text_field', editing.afterTreatment, original.afterTreatment);
      const nextBenefits = JSON.stringify(editing.benefits.filter((b) => b.trim()));
      const prevBenefits = JSON.stringify(original.benefits);
      pushIfChanged('benefits', 'list.single_line_text_field', nextBenefits, prevBenefits);
      // FAQ: sync editing.faq into metaobjects (update existing, create new,
      // drop removed), then store the resulting GID list on the product.
      const cleanFaq = editing.faq
        .map((f) => ({ q: (f.q ?? '').trim(), a: (f.a ?? '').trim() }))
        .filter((f) => f.q || f.a);

      // Determine metaobject `type` (needed for create). Prefer an existing
      // FAQ metaobject from this product; fall back to "faq".
      let faqType = 'faq';
      if (original.faqGids[0]) {
        try {
          const got = await shopifyAdmin<any>(METAOBJECT_GET, {
            id: original.faqGids[0],
          });
          if (got?.metaobject?.type) faqType = got.metaobject.type;
        } catch {
          // ignore — fall back to default
        }
      }

      const nextGids: string[] = [];
      for (let i = 0; i < cleanFaq.length; i++) {
        const item = cleanFaq[i];
        const existingGid = original.faqGids[i];
        const prevItem = original.faq[i];
        const changed =
          !prevItem || prevItem.q !== item.q || prevItem.a !== item.a;
        if (existingGid) {
          if (changed) {
            await shopifyAdmin(METAOBJECT_UPDATE, {
              id: existingGid,
              metaobject: {
                fields: [
                  { key: 'question', value: item.q },
                  { key: 'answer', value: item.a },
                ],
              },
            });
          }
          nextGids.push(existingGid);
        } else {
          const created = await shopifyAdmin<any>(METAOBJECT_CREATE, {
            metaobject: {
              type: faqType,
              fields: [
                { key: 'question', value: item.q },
                { key: 'answer', value: item.a },
              ],
            },
          });
          const newId = created?.metaobjectCreate?.metaobject?.id;
          if (newId) nextGids.push(newId);
        }
      }

      const nextFaqGids = JSON.stringify(nextGids);
      const prevFaqGids = JSON.stringify(original.faqGids);
      pushIfChanged('faqs', 'list.metaobject_reference', nextFaqGids, prevFaqGids);

      if (mfChanges.length) {
        await shopifyAdmin(METAFIELDS_SET, { metafields: mfChanges });
      }

      toast.success('נשמר בשופיפיי');
      setEditing(null);
      setOriginal(null);
      await load();
    } catch (e) {
      toast.error('שגיאה בשמירה', { description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 me-1 ${loading ? 'animate-spin' : ''}`} /> רענון
          </Button>
        </div>
      </div>

      {!loading && items.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap" dir="rtl">
          <div className="flex-1 min-w-[220px]">
            <Input
              placeholder={isService ? 'חיפוש טיפול…' : 'חיפוש מוצר…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Button
              size="sm"
              variant={categoryHandle === 'all' ? 'default' : 'outline'}
              onClick={() => setCategoryHandle('all')}
            >
              הכל ({items.length})
            </Button>
            {categories.map((c) => (
              <Button
                key={c.key}
                size="sm"
                variant={categoryHandle === c.key ? 'default' : 'outline'}
                onClick={() => setCategoryHandle(c.key)}
              >
                {c.title} ({c.count})
              </Button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">טוען…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">{emptyText}</div>
      ) : visibleItems.length === 0 ? (
        <div className="text-sm text-muted-foreground">לא נמצאו תוצאות לסינון הנוכחי.</div>
      ) : (
        <div className="space-y-8">
          {groupedItems.map((group) => (
            <div key={group.key} className="space-y-3">
              <div className="flex items-center gap-2 sticky top-0 bg-background/95 backdrop-blur py-2 z-10">
                <h2 className="text-sm font-semibold">{group.title}</h2>
                <Badge variant="outline" className="text-[10px]">
                  {group.items.length}
                </Badge>
                <div className="flex-1 border-t border-border ms-2" />
              </div>
              <div className="space-y-3">
                {group.items.map((sp) => {
            const variant = sp.variants.edges[0]?.node;
            const img = sp.images.edges[0]?.node?.url;
            const isOpen = editing?.productId === sp.id;
            return (
              <Card key={sp.id} className="p-4" dir="rtl">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-neutral-100 rounded overflow-hidden shrink-0">
                    {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">{sp.title}</div>
                      {variant?.compareAtPrice?.amount && (
                        <Badge variant="secondary">בהנחה</Badge>
                      )}
                      {sp.tags?.length > 0 &&
                        sp.tags.slice(0, 3).map((t) => (
                          <Badge key={t} variant="outline" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                    </div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-2 flex-wrap">
                      {(() => {
                         const raw = isService
                           ? serviceCategoryFromTags(sp.tags ?? [])
                           : sp.productType;
                         const label = raw ? categoryLabel(itemType, raw) : '';
                         return label ? (
                           <Badge variant="default" className="text-[10px]">
                             {label}
                           </Badge>
                         ) : null;
                       })()}
                      {productCollections(sp).map((c) => (
                        <Badge key={c.handle} variant="secondary" className="text-[10px]">
                          {c.title}
                        </Badge>
                      ))}
                      <span>
                        {isService ? '' : `SKU: ${variant?.sku || '—'} · `}₪{variant?.price.amount || '—'}
                      </span>
                    </div>
                  </div>
                  {isOpen ? (
                    <div className="flex items-center gap-2">
                      <Button onClick={save} disabled={saving} size="sm">
                        {saving ? 'שומר…' : 'שמירה'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditing(null);
                          setOriginal(null);
                        }}
                      >
                        <ChevronUp className="h-4 w-4 me-1" /> סגירה
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => startEdit(sp)}>
                      <Pencil className="h-4 w-4 me-1" /> עריכה
                    </Button>
                  )}
                </div>
                {isOpen && editing && (
                  <div className="mt-4 pt-4 border-t" dir="rtl">
                    <Tabs defaultValue="general" dir="rtl">
                      <TabsList className="grid grid-cols-4 w-full">
                        <TabsTrigger value="general">כללי</TabsTrigger>
                        <TabsTrigger value="price">מחיר</TabsTrigger>
                        <TabsTrigger value="media">מדיה</TabsTrigger>
                        <TabsTrigger value="content">תוכן עמוד</TabsTrigger>
                      </TabsList>




              <TabsContent value="general" className="space-y-4 pt-4">
                <Field label="שם">
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  />
                </Field>
                <CategoryField
                  itemType={itemType}
                  value={isService ? editing.serviceCategory : editing.productType}
                  onChange={(v) =>
                    setEditing(
                      isService
                        ? { ...editing, serviceCategory: v.toLowerCase() }
                        : { ...editing, productType: v },
                    )
                  }
                />
                <TagsField
                  value={editing.tags}
                  onChange={(tags) => setEditing({ ...editing, tags })}
                />
                <Field label="כותרת משנה">
                  <Input
                    value={editing.subtitle}
                    onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                  />
                </Field>
                <Field label="טאגליין (משפט קצר)">
                  <Input
                    value={editing.tagline}
                    onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                  />
                </Field>
                {!isService && (
                  <Field label="נפח / גודל">
                    <Input
                      value={editing.size}
                      onChange={(e) => setEditing({ ...editing, size: e.target.value })}
                      placeholder="לדוגמה: 50ml"
                    />
                  </Field>
                )}
                {isService && (
                  <Field label="משך הטיפול">
                    <Input
                      value={editing.duration}
                      onChange={(e) => setEditing({ ...editing, duration: e.target.value })}
                      placeholder="לדוגמה: 60 דק׳"
                    />
                  </Field>
                )}
                <Field label="תיאור קצר (HTML)">
                  <Textarea
                    rows={5}
                    value={editing.descriptionHtml}
                    onChange={(e) =>
                      setEditing({ ...editing, descriptionHtml: e.target.value })
                    }
                  />
                </Field>
              </TabsContent>

              <TabsContent value="price" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="מחיר (₪)">
                    <Input
                      type="number"
                      value={editing.price}
                      onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    />
                  </Field>
                  <Field label="מחיר לפני הנחה (₪)">
                    <Input
                      type="number"
                      value={editing.compareAtPrice}
                      onChange={(e) =>
                        setEditing({ ...editing, compareAtPrice: e.target.value })
                      }
                      placeholder="ריק = ללא הנחה"
                    />
                  </Field>
                </div>
                {!isService && (
                  <Field label="SKU">
                    <Input
                      value={editing.sku}
                      onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                    />
                  </Field>
                )}
                {editing.compareAtPrice && Number(editing.compareAtPrice) > Number(editing.price) && (
                  <div className="text-xs text-emerald-700">
                    הנחה:{' '}
                    {Math.round(
                      ((Number(editing.compareAtPrice) - Number(editing.price)) /
                        Number(editing.compareAtPrice)) *
                        100,
                    )}
                    %
                  </div>
                )}
              </TabsContent>

              <TabsContent value="media" className="space-y-4 pt-4">
                <div>
                  <Label className="text-xs">תמונות (URL)</Label>
                  <div className="space-y-2 mt-1">
                    {editing.imageUrls.map((url, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-14 h-14 bg-neutral-100 rounded overflow-hidden shrink-0">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        </div>
                        <Input
                          value={url}
                          onChange={(e) => {
                            const arr = [...editing.imageUrls];
                            arr[i] = e.target.value;
                            setEditing({ ...editing, imageUrls: arr });
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setEditing({
                              ...editing,
                              imageUrls: editing.imageUrls.filter((_, idx) => idx !== i),
                            })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setEditing({ ...editing, imageUrls: [...editing.imageUrls, ''] })
                      }
                    >
                      <Plus className="h-4 w-4 me-1" /> הוספת תמונה
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    הדבק/י כתובת URL של תמונה. שופיפיי יטען אותה אוטומטית.
                  </p>
                </div>
                <Field label="וידאו (URL)">
                  <Input
                    value={editing.videoUrl}
                    onChange={(e) => setEditing({ ...editing, videoUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </Field>
              </TabsContent>

              <TabsContent value="content" className="space-y-4 pt-4">
                <Field label="תיאור מורחב (פסקאות מופרדות בשורה ריקה)">
                  <Textarea
                    rows={6}
                    value={editing.longDescription}
                    onChange={(e) =>
                      setEditing({ ...editing, longDescription: e.target.value })
                    }
                  />
                </Field>
                {!isService && (
                  <Field label="רכיבים">
                    <Textarea
                      rows={4}
                      value={editing.ingredients}
                      onChange={(e) =>
                        setEditing({ ...editing, ingredients: e.target.value })
                      }
                    />
                  </Field>
                )}
                <Field label={isService ? 'מהלך הטיפול / מה כולל' : 'אופן השימוש'}>
                  <Textarea
                    rows={4}
                    value={editing.howToUse}
                    onChange={(e) => setEditing({ ...editing, howToUse: e.target.value })}
                  />
                </Field>
                {isService && (
                  <>
                    <Field label="למי הטיפול מתאים">
                      <Textarea
                        rows={3}
                        value={editing.suitableFor}
                        onChange={(e) => setEditing({ ...editing, suitableFor: e.target.value })}
                      />
                    </Field>
                    <Field label="לפני הטיפול">
                      <Textarea
                        rows={3}
                        value={editing.beforeTreatment}
                        onChange={(e) => setEditing({ ...editing, beforeTreatment: e.target.value })}
                      />
                    </Field>
                    <Field label="אחרי הטיפול">
                      <Textarea
                        rows={3}
                        value={editing.afterTreatment}
                        onChange={(e) => setEditing({ ...editing, afterTreatment: e.target.value })}
                      />
                    </Field>
                  </>
                )}

                <div>
                  <Label className="text-xs">יתרונות (Benefits)</Label>
                  <div className="space-y-2 mt-1">
                    {editing.benefits.map((b, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          value={b}
                          onChange={(e) => {
                            const arr = [...editing.benefits];
                            arr[i] = e.target.value;
                            setEditing({ ...editing, benefits: arr });
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setEditing({
                              ...editing,
                              benefits: editing.benefits.filter((_, idx) => idx !== i),
                            })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setEditing({ ...editing, benefits: [...editing.benefits, ''] })
                      }
                    >
                      <Plus className="h-4 w-4 me-1" /> הוספת יתרון
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">שאלות נפוצות (FAQ)</Label>
                  <div className="space-y-3 mt-1">
                    {editing.faq.map((f, i) => (
                      <div key={i} className="border rounded p-2 space-y-2">
                        <Input
                          placeholder="שאלה"
                          value={f.q}
                          onChange={(e) => {
                            const arr = [...editing.faq];
                            arr[i] = { ...f, q: e.target.value };
                            setEditing({ ...editing, faq: arr });
                          }}
                        />
                        <Textarea
                          rows={2}
                          placeholder="תשובה"
                          value={f.a}
                          onChange={(e) => {
                            const arr = [...editing.faq];
                            arr[i] = { ...f, a: e.target.value };
                            setEditing({ ...editing, faq: arr });
                          }}
                        />
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setEditing({
                                ...editing,
                                faq: editing.faq.filter((_, idx) => idx !== i),
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4 me-1" /> הסרה
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setEditing({
                          ...editing,
                          faq: [...editing.faq, { q: '', a: '' }],
                        })
                      }
                    >
                      <Plus className="h-4 w-4 me-1" /> הוספת שאלה
                    </Button>
                  </div>
                </div>
              </TabsContent>
                    </Tabs>
                  </div>
                )}
              </Card>
            );
          })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function CategoryField({
  itemType,
  value,
  onChange,
}: {
  itemType: 'product' | 'service';
  value: string;
  onChange: (v: string) => void;
}) {
  const options =
    itemType === 'service' ? SERVICE_CATEGORY_OPTIONS : PRODUCT_CATEGORY_OPTIONS;
  return (
    <Field label="קטגוריה (productType בשופיפיי — קובע את שיוך העמוד באתר)">
      <div className="flex flex-wrap gap-2 mb-2">
        {options.map((o) => (
          <Button
            key={o.value}
            type="button"
            size="sm"
            variant={
              value.toLowerCase() === o.value.toLowerCase() ? 'default' : 'outline'
            }
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="או הקלידי ערך חופשי"
      />
    </Field>
  );
}

function TagsField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (value.includes(t)) {
      setDraft('');
      return;
    }
    onChange([...value, t]);
    setDraft('');
  };
  return (
    <Field label="תגיות (Tags — מילות מפתח לחיפוש וסינון)">
      <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
        {value.length === 0 && (
          <span className="text-xs text-muted-foreground">אין תגיות</span>
        )}
        {value.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1">
            {t}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== t))}
              className="ms-1 text-muted-foreground hover:text-foreground"
              aria-label={`הסר ${t}`}
            >
              ×
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          placeholder="הוסיפי תגית ולחצי Enter"
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>
          הוספה
        </Button>
      </div>
    </Field>
  );
}
